import { signal } from '@preact/signals';
import { services } from '../app';
import type { SkyEvent } from '../astro/events';
import type { Place } from '../astro/frame';
import { sunsetDay } from '../astro/hijri';
import { loadJSON, place, previewFrom, reliving, saveJSON, settings } from '../state';
import { directionWords } from '../ui/format';
import { FRESH_ORBIT_AGE } from './orbitAge';
import { IA } from '../lib/reminders';
import { EventReminder, alertable, askedFor, askedReminders, dateIn, isAsked, remindersFor, remindersToSet, setAsked, timeIn, toggleAsked, tzOf, whiteDaysReminders } from './skycalendar';
import { eventsAroundLater } from './skyevents';

// Alerts that arrive while Ahillah is closed, through Web Push: "clear skies tonight" at about 4 pm,
// a reminder ten minutes before each bright ISS pass, and notes before notable sky events (and any the
// person asked for in the sky calendar). The phone works these out and sends them with the subscription;
// the server keeps the subscription, a location rounded to about 10 km, the time zone and those
// reminders, and nothing else.

export type AlertState = 'off' | 'on' | 'working' | 'unsupported' | 'install' | 'denied' | 'unavailable';
export const alertState = signal<AlertState>('off');

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

function supported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Work out the current state (on start-up and when the settings open). */
export async function refreshAlertState() {
  if (!supported()) {
    alertState.value = isIOS() && !standalone() ? 'install' : 'unsupported';
    return;
  }
  if (Notification.permission === 'denied') {
    alertState.value = 'denied';
    return;
  }
  alertState.value = (await currentSubscription()) ? 'on' : 'off';
}

const toKey = (b64: string) => {
  const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

const toB64u = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** Home, never a place being previewed. */
const home = (): Place => previewFrom.peek()?.home ?? place.peek();

const ISS = 25544;

/** Reminders for the bright ISS passes over the next three days, ten minutes ahead of each. Each pass needs an orbit
 *  measured within three days of it: the station's orbit changes with each boost and with the drag of the air, so
 *  pass times worked out from an older one drift. */
async function issReminders(p: Place) {
  const s = services.peek();
  const iss = s?.sats.meta.get(ISS);
  const now = Date.now();
  if (!s || !iss || Math.abs(now - iss.epoch) > FRESH_ORBIT_AGE) return [];
  const passes = await s.sats.passes([ISS], new Date(now), 72, 4.5, { lat: p.lat, lon: p.lon, height: p.height });
  const tz = p.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const time = (t: number) => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(t);
  return passes
    .filter((x) => x.visStart && x.visEnd && x.visEnd - x.visStart >= 90e3 && x.peakEl >= 20 && (x.minMag ?? 9) <= -0.5 && x.visStart - 10 * 60e3 > now && Math.abs(x.visStart - iss.epoch) < FRESH_ORBIT_AGE)
    .slice(0, 12)
    .map((x) => {
      const mag = x.minMag!;
      const bright = mag <= -3 ? 'Brilliant' : mag <= -2 ? 'Very bright' : 'Bright';
      return {
        at: x.visStart! - 10 * 60e3,
        until: x.visEnd!,
        title: `The ISS passes over in 10 minutes, ${IA}`,
        body: `From ${time(x.visStart!)} in the ${directionWords(x.visStartAz)}, climbing to ${Math.round(x.peakEl)}° and gone by ${time(x.visEnd!)} in the ${directionWords(x.visEndAz)}. ${bright}: it looks like a steady, fast-moving star.`,
        tag: `iss-${Math.round(x.visStart! / 1000)}`,
        url: `/?at=${encodeURIComponent(new Date(x.visStart! - 30e3).toISOString())}&sel=sat:${ISS}`,
        clear: true,
        lat: Math.round(p.lat * 10) / 10,
        lon: Math.round(p.lon * 10) / 10,
      };
    });
}

/** How long after its time a reminder asked for may first go to the server: one that missed it by minutes still
 *  goes, a little late, but never one so late that its words no longer fit. */
const ASKED_GRACE = 15 * 60e3;

/** A reminder as the server knows it: its tag and time (one moved to a later time is another reminder). */
const keyOf = (r: { tag: string; at: number }) => `${r.tag}@${r.at}`;
const keysOf = (list: { tag: string; at: number }[]) => list.map(keyOf).sort();
/** The reminders asked for that are not yet out of date. */
const askedNow = (now = Date.now()) => askedReminders.peek().filter((r) => r.until > now);

let lastSync = 0;
/** The reminders asked for as the server last heard them (`tag@at`), whether or not it could take each (one beyond its
 *  six weeks waits on this phone): a change since then is still to go. */
const heard = signal(loadJSON<string[]>('remindersHeard', []));
/** Those the server has: in the list it took. Only these are called set. */
const held = signal(loadJSON<string[]>('remindersHeld', []));
/** Whether a reminder was set or taken away on this phone since the server last heard. */
export const remindersPending = () => keysOf(askedNow()).join(' ') !== heard.peek().join(' ');
/** Whether the server has an event's reminder (one set offline, or beyond six weeks, waits on this phone until it can go). */
export function reminderSent(e: SkyEvent, p: Place) {
  const have = new Set(held.value);
  return askedFor(e, p).every((r) => have.has(keyOf(r)));
}

/**
 * Notes before the notable sky events of the next six weeks at home, and those in `asked` (the reminders asked for
 * with "Remind me", as read when the sync began). Throws if the events could not be worked out, so the sync stops
 * rather than tell the server there are none.
 */
async function skyReminders(p: Place, asked: EventReminder[]) {
  const s = services.peek();
  const set = settings.peek();
  const now = Date.now();
  const have = new Set(held.peek());
  // Those asked for say so: the server sends them whatever the weather and tries again if sending fails. One the
  // server has stays in the list until it is out of date (left out, the server would take it as taken away and drop
  // its next try); one it never had goes only until a quarter of an hour after its time.
  const mine: EventReminder[] = asked.filter((r) => r.at > now - ASKED_GRACE || have.has(keyOf(r))).map((r) => ({ ...r, asked: true }));
  const auto: EventReminder[] = [];
  if (s && set.pushEvents) {
    // From the events worker, so a sync never holds up the sky.
    const near = await eventsAroundLater(p, now, s.meteors.showers, set.bortle);
    if (!near) throw new Error('Sky events could not be worked out');
    for (const e of near) if (alertable(e)) for (const r of remindersFor(e, p)) if (r.at > now && !mine.some((x) => x.tag === r.tag) && !auto.some((x) => x.tag === r.tag)) auto.push(r);
  }
  // The server's six weeks; those asked for first, then the automatic ones while there is room (48 in all).
  const within = (r: EventReminder) => r.at < now + 44 * 86400e3;
  const kept = mine.filter(within);
  return [...kept, ...auto.filter(within).sort((a, b) => a.at - b.at).slice(0, Math.max(0, 48 - kept.length))].sort((a, b) => a.at - b.at);
}

/** The event a reminder is for, from its own link. */
const evOf = (r: { url: string }) => new URL(r.url, 'https://ahillah.invalid').searchParams.get('ev') ?? '';
/** Events dated by Saudi Arabia's calendar: the crescent's evening, ʿArafah and ʿĀshūrāʾ. */
const hijriDated = (id: string) => /^(crescent|day)-/.test(id);

/** Reminders asked for an event dated by Saudi Arabia's calendar follow its decisions: each is worked out again from
 *  its event as it now stands (the event keeps its id when a month moves), and kept so on this phone. */
async function refreshHijriAsked(p: Place) {
  const s = services.peek();
  const list = askedReminders.peek();
  if (!s || !list.some((r) => hijriDated(evOf(r)))) return;
  const near = await eventsAroundLater(p, Date.now(), s.meteors.showers, settings.peek().bortle);
  if (!near) return;
  const byId = new Map(near.map((e) => [e.id, e]));
  let changed = false;
  const next = list.map((r) => {
    const id = evOf(r);
    const e = hijriDated(id) ? byId.get(id) : undefined;
    const fresh = e && remindersFor(e, p).find((x) => x.tag === r.tag);
    // Kept as it is unless its day has moved (one set late, for a few minutes' time, keeps its time). One for an hour
    // before sunset belongs to that sunset's date, also after midnight before a late northern sunset (server/alerts.ts).
    const tz = tzOf(p);
    const w = { lat: p.lat, lon: p.lon, tz };
    const day = (t: number) => (r.hijri && r.hijri.h < 0 ? sunsetDay(t, w) : dateIn(t, tz));
    if (!fresh || day(fresh.at) === day(r.at)) return r;
    changed = true;
    return { ...r, ...fresh };
  });
  if (changed) setAsked(next);
}

let queue: Promise<unknown> = Promise.resolve();
/**
 * Send the server the current place, preferences and reminders. One sync at a time, each made up when its turn
 * comes: the server always ends with the newest list, and an answer never vouches for a change made after it.
 * A turn stuck on a stalled connection is given up after 15 seconds (and then sends nothing), so it cannot hold
 * up the ones after it.
 */
export function syncAlerts(force = false): Promise<boolean> {
  const turn = queue
    .then(async () => {
      const stop = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      const late = new Promise<boolean>((r) => (timer = setTimeout(() => (stop.abort(), r(false)), 15e3)));
      try {
        return await Promise.race([sync(force, stop.signal), late]);
      } finally {
        clearTimeout(timer);
      }
    })
    .catch(() => false);
  queue = turn;
  return turn;
}

async function sync(force: boolean, signal: AbortSignal): Promise<boolean> {
  if (!supported() || Notification.permission !== 'granted') return false;
  // While a saved night is relived, only its own satellites (with old orbit data) are loaded: wait.
  if (reliving.peek()) return false;
  let sub = await currentSubscription();
  if (!sub) return false;
  if (!force && !remindersPending() && Date.now() - lastSync < 20 * 60e3) return true;
  // If the server's key has changed since this device subscribed, its pushes would be refused:
  // subscribe again with the new key.
  try {
    const k = await fetch('/api/push/key', { signal });
    if (k.ok) {
      const { key } = (await k.json()) as { key: string };
      const cur = sub.options?.applicationServerKey;
      if (cur && toB64u(new Uint8Array(cur)) !== key.replace(/=+$/, '')) {
        await sub.unsubscribe();
        const reg = await navigator.serviceWorker.ready;
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(key) });
      }
    }
  } catch {
    /* keep the old subscription; the server tidies up if it has gone */
  }
  const p = home();
  const set = settings.peek();
  try {
    await refreshHijriAsked(p);
    // The reminders asked for, read once before any wait: the list sent and what the answer vouches for are the same,
    // and one set or taken away meanwhile is still to go (its own sync follows this one).
    const asked = askedNow();
    const iss = set.pushIss ? await issReminders(p) : [];
    const sky = await skyReminders(p, asked);
    const r = await fetch('/api/push/sync', {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sub: sub.toJSON(),
        lat: Math.round(p.lat * 10) / 10,
        lon: Math.round(p.lon * 10) / 10,
        tz: p.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        evening: set.pushEvening,
        // Saudi Arabia's month alerts, sent by the server from the announcements themselves.
        sought: set.pushSought,
        others: set.pushMonths,
        // Silent unless a sound is chosen (BUILD-SPEC item 18).
        sound: set.pushSound,
        reminders: [...iss, ...sky, ...(set.pushWhiteDays ? whiteDaysReminders(p, Date.now()) : [])],
      }),
    });
    if (r.ok) {
      lastSync = Date.now();
      heard.value = keysOf(asked);
      held.value = keysOf(sky.filter((x) => x.asked));
      saveJSON('remindersHeard', heard.value);
      saveJSON('remindersHeld', held.value);
    }
    return r.ok;
  } catch {
    return false;
  }
}

// A reminder set or taken away offline goes to the server as soon as the phone is back online (and otherwise at
// the next sync: Ahillah syncs when it opens and when it comes back to the screen).
try {
  addEventListener('online', () => void (remindersPending() && syncAlerts(true)));
} catch {
  /* no window (tests) */
}

/**
 * "Remind me" in the sky calendar, or taking the reminder away again. The change is kept on this phone at once,
 * but called done only once Ahillah's server has it; until then it waits here and goes at the next chance.
 * Returns what to tell the person.
 */
export async function toggleReminder(e: SkyEvent, p: Place, elsewhere: boolean): Promise<{ text: string; ms?: number }> {
  const offline = () => navigator.onLine === false;
  if (isAsked(e, p)) {
    toggleAsked(e, p);
    // One asked for "even if cloudy" leaves the automatic one (Sky events), which comes only when the sky looks clear.
    const now = Date.now();
    const auto = !elsewhere && !!services.peek() && alertState.peek() === 'on' && settings.peek().pushEvents && alertable(e) && remindersFor(e, p).some((r) => r.clear && r.at > now);
    if (alertState.peek() !== 'on' || (await syncAlerts(true))) return { text: auto ? 'Removed. Ahillah still reminds you about this one if the sky looks clear.' : 'Reminder removed' };
    return {
      text: offline()
        ? 'Removed on this phone. Ahillah will tell the server when you are back online, so the reminder may still arrive until then.'
        : 'Removed on this phone, but the server has not heard yet, so the reminder may still arrive. Ahillah will try again next time you open it.',
      ms: 7000,
    };
  }
  // Those still to come; or, once they have all gone, one later reminder while the event is not over.
  const plan = remindersToSet(e, p);
  if (!plan.list.length) return { text: 'It is too late to set a reminder for this' };
  const st = alertState.peek();
  if (st === 'install') return { text: 'On iPhone, first add Ahillah to your Home Screen (Share, then Add to Home Screen) to get reminders. Or use Add to calendar.', ms: 7000 };
  if (st === 'unsupported') return { text: 'This browser cannot send reminders. Use Add to calendar instead.', ms: 5000 };
  if (st === 'denied') return { text: 'Notifications are blocked for Ahillah in your phone or browser settings. Add to calendar works without them.', ms: 6000 };
  toggleAsked(e, p, plan.list);
  const first = plan.list[0];
  // Beyond the server's six weeks, a reminder waits on this phone until Ahillah is opened nearer the time.
  const far = first.at - Date.now() > 44 * 86400e3;
  if (st !== 'on') {
    await enableAlerts();
    const now = alertState.peek();
    if (now !== 'on') {
      toggleAsked(e, p);
      return {
        text: now === 'denied' ? 'Notifications are blocked for Ahillah, so no reminder was set. Add to calendar works without them.'
          : now === 'off' ? 'No reminder set: notifications were not allowed.'
          : 'Could not turn on notifications just now. Try again later, or use Add to calendar.',
        ms: 5000,
      };
    }
  } else if (!(await syncAlerts(true)) && !far) {
    return {
      text: offline()
        ? 'Saved on this phone. It will be sent to the server when you are back online.'
        : 'Saved on this phone, but it has not reached the server yet. Ahillah will try again next time you open it.',
      ms: 6000,
    };
  }
  const tz = tzOf(p);
  // In the hour the clocks go back, 01:30 comes twice: say which.
  const timeOf = (t: number) => {
    const at = timeIn(t, tz);
    if (timeIn(t - 3600e3, tz) !== at && timeIn(t + 3600e3, tz) !== at) return at;
    return `${at} ${new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' }).formatToParts(t).find((x) => x.type === 'timeZoneName')?.value ?? ''}`.trim();
  };
  if (far) return { text: `Saved on this phone for ${dateIn(first.at, tz)}. Ahillah will pass it to the server when you open the app nearer the time; for a reminder you cannot miss, also use Add to calendar.`, ms: 9000 };
  const zone = elsewhere ? ` (${p.name} time)` : '';
  if (!plan.late) return { text: `Reminder set for ${dateIn(first.at, tz)} at ${timeOf(first.at)}${zone}`, ms: 3500 };
  // Set late, the reminder is within the day: its time, and the date only if it is not today's.
  const day = dateIn(first.at, tz) === dateIn(Date.now(), tz) ? '' : `${dateIn(first.at, tz)} at `;
  return { text: `Reminder set for ${day}${timeOf(first.at)}${zone}, ${plan.late === 'hour' ? 'an hour before its best time' : 'in a few minutes'}`, ms: 5000 };
}

export async function enableAlerts() {
  if (!supported()) return refreshAlertState();
  alertState.value = 'working';
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      alertState.value = perm === 'denied' ? 'denied' : 'off';
      return;
    }
    const k = await fetch('/api/push/key');
    if (!k.ok) {
      alertState.value = 'unavailable';
      return;
    }
    const { key } = (await k.json()) as { key: string };
    // The service worker may still be installing on a first visit; do not wait for ever.
    const reg = await Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 10000))]);
    if (!reg) {
      alertState.value = 'unavailable';
      return;
    }
    (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(key) }));
    alertState.value = (await syncAlerts(true)) ? 'on' : 'unavailable';
  } catch {
    alertState.value = 'unavailable';
  }
}

export async function disableAlerts() {
  alertState.value = 'working';
  // In its turn after any sync already on its way: one that landed after this would bring the server's copy back.
  const turn = queue
    .then(async () => {
      const sub = await currentSubscription();
      if (sub) {
        await fetch('/api/push/unsubscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => {});
        await sub.unsubscribe();
      }
      // The server has forgotten the reminders, so the phone does too: none is shown as set while nothing can send it.
      setAsked([]);
      heard.value = held.value = [];
      saveJSON('remindersHeard', []);
      saveJSON('remindersHeld', []);
    })
    .catch(() => {});
  queue = turn;
  try {
    await turn;
  } finally {
    await refreshAlertState();
  }
}

/** Ask the server for a notification now, so people can see alerts work (and how they look). */
export async function testAlert(): Promise<string> {
  const sub = await currentSubscription();
  if (!sub) return 'Alerts are off';
  try {
    const r = await fetch('/api/push/test', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint }) });
    if (r.status === 429) return 'Please wait a minute before trying again';
    if (r.status === 425) return 'Your device is still registering for alerts: try again in a minute';
    if (r.status === 410) {
      await refreshAlertState();
      return 'This device’s alert address has expired: turn alerts off and on again';
    }
    // The server answers 200 whenever it could ask; whether the push service took the message is in the body.
    const body = (await r.json().catch(() => null)) as { ok?: unknown } | null;
    return r.ok && body?.ok === true ? 'Sent: it should arrive in a few seconds' : 'Could not send a test just now';
  } catch {
    return 'Could not reach Ahillah’s server';
  }
}

/** Keep the server's copy current: when the home place or preferences change, and on each visit. */
export const updateAlertPlace = () => syncAlerts(true);
